import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { TournamentSummary } from '../types';
import { Loading } from '../components/Loading';
import { Notice } from '../components/Notice';
import { useAuth } from '../context/AuthContext';

const formatLabel = (value: string) => value.replace('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

function TournamentCard({ item }: { item: TournamentSummary }) {
  return (
    <Link to={`/tournaments/${item.id}`} key={item.id} className="surface group block p-5 transition hover:-translate-y-1 hover:shadow-lift">
      <div className="flex items-start justify-between gap-3"><span className={`chip ${item.status === 'active' ? 'bg-emerald-100 text-emerald-700' : item.status === 'completed' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>{formatLabel(item.status)}</span><span className="text-2xl transition group-hover:translate-x-1">→</span></div>
      <h4 className="mt-5 text-xl font-extrabold tracking-tight brand-text">{item.name}</h4>
      <p className="mt-1 text-sm font-semibold text-slate-400">{formatLabel(item.playType)} · {item.playType === 'doubles' ? formatLabel(item.partnerMode) : 'Individual'}</p>
      {(item.scheduledDate || item.venue) && <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-bold text-slate-500">
        {item.scheduledDate && <span className="flex items-center gap-1"><span className="text-[10px]">📅</span> {new Date(item.scheduledDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>}
        {item.venue && <span className="flex items-center gap-1"><span className="text-[10px]">📍</span> {item.venue}</span>}
      </div>}
      <div className="mt-6 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4 text-center"><div><b className="block brand-text">{item.playerCount}</b><span className="text-[10px] font-bold uppercase text-slate-400">Players</span></div><div><b className="block brand-text">{item.courts}</b><span className="text-[10px] font-bold uppercase text-slate-400">Courts</span></div><div><b className="block brand-text">{item.currentRound}/{item.rounds}</b><span className="text-[10px] font-bold uppercase text-slate-400">Round</span></div></div>
    </Link>
  );
}

function AdminAccordion({ items }: { items: TournamentSummary[] }) {
  // Group tournaments by owner
  const grouped = new Map<string, { username: string; tournaments: TournamentSummary[] }>();
  for (const item of items) {
    const key = item.ownerUserId ?? 'unknown';
    if (!grouped.has(key)) grouped.set(key, { username: item.ownerUsername ?? key, tournaments: [] });
    grouped.get(key)!.tournaments.push(item);
  }
  const groups = [...grouped.entries()];

  const [open, setOpen] = useState<Set<string>>(new Set(groups.map(([k]) => k)));
  const allOpen = open.size === groups.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <p className="eyebrow">All events by organiser</p>
        <button onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map(([k]) => k)))} className="text-xs font-bold brand-text hover:opacity-70 transition">
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      {groups.map(([userId, { username, tournaments }]) => {
        const isOpen = open.has(userId);
        const live = tournaments.filter((t) => t.status === 'active').length;
        return (
          <div key={userId} className="surface overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition"
              onClick={() => setOpen((prev) => { const next = new Set(prev); isOpen ? next.delete(userId) : next.add(userId); return next; })}
            >
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-2xl brand-bg font-extrabold text-white text-sm">{username[0].toUpperCase()}</span>
                <div>
                  <p className="font-extrabold brand-text">{username}</p>
                  <p className="text-xs font-semibold text-slate-400">{tournaments.length} event{tournaments.length !== 1 ? 's' : ''}{live > 0 ? ` · ${live} live` : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {live > 0 && <span className="chip bg-emerald-100 text-emerald-700 text-[10px]">{live} Live</span>}
                <span className={`text-slate-400 text-lg transition-transform ${isOpen ? 'rotate-180' : ''}`}>⌄</span>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-100 p-4">
                {tournaments.length === 0 ? (
                  <p className="text-sm text-slate-400 py-2">No events yet.</p>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {tournaments.map((item) => <TournamentCard key={item.id} item={item} />)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<TournamentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'upcoming'>('all');
  
  useEffect(() => { api<{ tournaments: TournamentSummary[] }>('/tournaments').then((response) => setItems(response.tournaments)).catch((value) => setError(value.message)).finally(() => setLoading(false)); }, []);
  if (loading) return <Loading label="Loading your tournaments…" />;

  const active = items.filter((item) => item.status === 'active').length;
  
  const today = new Date().toISOString().split('T')[0];
  const displayedItems = filter === 'upcoming' 
    ? items.filter(item => item.scheduledDate && item.scheduledDate >= today).sort((a, b) => (a.scheduledDate ?? '').localeCompare(b.scheduledDate ?? ''))
    : items;
    
  return <div className="space-y-7">
    <section className="surface-dark relative overflow-hidden p-6 sm:p-8">
      <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full border-[48px] border-white/5" />
      <div className="relative flex flex-col justify-between gap-7 sm:flex-row sm:items-end">
        <div><p className="text-xs font-extrabold uppercase tracking-[0.18em] text-white/50">Good to see you, {user?.username}</p><h2 className="mt-3 max-w-2xl text-3xl font-extrabold tracking-tight sm:text-4xl">Ready to get everyone <span className="accent-text">on court?</span></h2><p className="mt-3 max-w-xl text-sm leading-6 text-white/60">Create a fair draw in seconds, then run the whole session from your phone.</p></div>
        <Link className="btn-primary shrink-0" to="/tournaments/new">+ New tournament</Link>
      </div>
    </section>

    {error && <Notice>{error}</Notice>}
    <section className="grid grid-cols-3 gap-3 sm:gap-5">
      {[['Total', items.length], ['Live', active], ['Players', items.reduce((sum, item) => sum + item.playerCount, 0)]].map(([label, value]) => <div className="surface p-4 sm:p-6" key={label}><p className="text-2xl font-extrabold brand-text sm:text-3xl">{value}</p><p className="mt-1 text-xs font-bold text-slate-400">{label}</p></div>)}
    </section>

    {user?.role === 'admin' ? (
      <section>
        <AdminAccordion items={items} />
      </section>
    ) : (
      <section>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div><p className="eyebrow">Your events</p><h3 className="mt-1 text-2xl font-extrabold brand-text">Tournament board</h3></div>
          <div className="flex rounded-xl bg-slate-200/60 p-1">
            <button onClick={() => setFilter('all')} className={`rounded-lg px-4 py-2 text-xs font-extrabold transition ${filter === 'all' ? 'bg-white brand-text shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>All events</button>
            <button onClick={() => setFilter('upcoming')} className={`rounded-lg px-4 py-2 text-xs font-extrabold transition ${filter === 'upcoming' ? 'bg-white brand-text shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Upcoming</button>
          </div>
        </div>
        {displayedItems.length === 0 ? <div className="surface grid min-h-64 place-items-center p-8 text-center"><div><div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-3xl">🏓</div><h4 className="mt-4 text-lg font-extrabold brand-text">No {filter === 'upcoming' ? 'upcoming' : ''} tournaments</h4><p className="mt-2 text-sm text-slate-500">{filter === 'upcoming' ? 'Schedule a new event in the future.' : 'Add your players and generate the first draw.'}</p><Link to="/tournaments/new" className="btn-dark mt-5">Create tournament</Link></div></div> : <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {displayedItems.map((item) => <TournamentCard key={item.id} item={item} />)}
        </div>}
      </section>
    )}
  </div>;
}
